import { THIS_EXPR } from "@angular/compiler/src/output/output_ast";
import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { ThrottlingUtils } from "@azure/msal-common";
import { faGrinTongueSquint } from "@fortawesome/free-solid-svg-icons";
import { Observable, Subject, Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { BoletaHonorariosEmitida } from "src/app/models/BoletaHonorarios/BoletaHonorariosEmitida";
import { DatosCreacionBhe } from "src/app/models/BoletaHonorarios/DatosCreacionBhe";
import { DatosEmisorBhe } from "src/app/models/BoletaHonorarios/DatosEmisorBhe";
import { ResumenDetallesBhe } from "src/app/models/BoletaHonorarios/ResumenDetallesBhe";
import { BheService } from "src/app/services/bhe.service";
import { EmisionBheService } from "src/app/services/emision-bhe.service";
import { TokenStorageService } from "src/app/services/token-storage.service";
import { UtilitiesService } from "src/app/services/utilities.service";
import { InvoicingButton } from "src/app/shared/models/InvoicingButton.enum";
import Swal from "sweetalert2";
import { SessionUserService } from 'src/app/modules/auth/session-user/session-user.service';

@Component({
	selector: "app-emisor-boleta-honorarios",
	templateUrl: "./emisor-boleta-honorarios.component.html",
	styleUrls: ["./emisor-boleta-honorarios.component.scss"],
})
export class EmisorBoletaHonorariosComponent implements OnInit {
	public loading: boolean = false;
	public buttonAction$: Observable<InvoicingButton>;
	private datosEmisor$: Observable<DatosEmisorBhe>;
	private datosReceptor$: Observable<DatosCreacionBhe>;
	private datosResumenDetalles: Observable<ResumenDetallesBhe>;
	private destroySubscriptions$: Subject<boolean> = new Subject<boolean>();
	private subscriptions: Subscription[] = [];
	private newBhe: BoletaHonorariosEmitida = new BoletaHonorariosEmitida();
	private currentUsuarioId = this.tokenStorage.getUser().id;
	constructor(private emisionBheService: EmisionBheService, private router: Router, private bheService: BheService, private ref: ChangeDetectorRef, private utilities: UtilitiesService, private tokenStorage: TokenStorageService, private sessionUserService: SessionUserService) {
		this.buttonAction$ = this.emisionBheService.buttonBheActionObservable;
		this.datosEmisor$ = this.emisionBheService.datosEmisorBheObservable;
		this.datosReceptor$ = this.emisionBheService.datosReceptorObservable;
		this.datosResumenDetalles = this.emisionBheService.resumenDetallesBheObservable;
	}

	ngOnInit(): void {
		this.sessionUserService.verificarSesionUsuario();
		const subscriptionButtonActions = this.buttonAction$.pipe(takeUntil(this.destroySubscriptions$)).subscribe((action) => {
			if (action == InvoicingButton.Emitir) {
				this.consolidateBhe();
			}
			if (action == InvoicingButton.Cancelar) {
				this.returnToMaintainer();
			}
		});
		const subscriptionDatosEmisor = this.datosEmisor$.subscribe((datos) => {
			this.emisorToRequest(datos);
		});
		const subscriptionDatosCreacion = this.datosReceptor$.subscribe((datos) => {
			this.datosCreacionToRequest(datos);
		});
		const subscriptionDatosResumenDetalles = this.datosResumenDetalles.subscribe((resumen) => {
			this.resumenBheToRequest(resumen);
		});

		this.subscriptions.push(subscriptionButtonActions);
		this.subscriptions.push(subscriptionDatosEmisor);
		this.subscriptions.push(subscriptionDatosCreacion);
		this.subscriptions.push(subscriptionDatosResumenDetalles);
	}

	consolidateBhe() {
		if (this.validBheToSend()) {
			this.sendBhe();
		}
	}

	validBheToSend(): boolean {
		if (!this.validEmisorDataInRequest()) {
			Swal.fire({
				icon: "error",
				title: "Error en datos de emisor",
				text: "No se han agregado todos los datos del emisor asociado",
			});
			return false;
		} else if (!this.validCreacionDataInRequest()) {
			Swal.fire({
				icon: "error",
				title: "Error en datos de creación",
				text: "Agregue los datos del receptor de la boleta de honorarios a emitir",
			});
			return false;
		} else if (!this.validDetallesListInRequest()) {
			Swal.fire({
				icon: "error",
				title: "Error en detalles",
				text: "La lista de prestaciones no contiene datos",
			});
			return false;
		} else if (!this.validResumenDataInRequest()) {
			Swal.fire({
				icon: "error",
				title: "Error en totales",
				text: "El total de los elementos no ha sido calculado",
			});
			return false;
		} else {
			return true;
		}
	}
	sendBhe() {
		this.loading = true;
		this.newBhe.usuarioId = this.currentUsuarioId;
		this.bheService.sendBhe(this.newBhe).subscribe(
			(result) => {
				if (result.data) {
					this.loading = false;
					this.ref.detectChanges();
					Swal.fire({
						icon: "success",
						title: "Emisión Exitosa",
						text: "Boleta de Honorarios emitida correctamente",
					});
					this.returnToMaintainer();
					this.emisionBheService.resetServiceData();
				}
			},
			(error) => {
				this.loading = false;
				this.ref.detectChanges();
				Swal.fire({
					icon: "error",
					title: "Error en emisión",
					text: "No fue posible emitir la boleta de honorarios. " + this.utilities.setErrorMessageFromArray(error.error),
				});
			}
		);
	}

	returnToMaintainer() {
		this.router.navigateByUrl("/ventas/boletas-honorarios-emitidas");
	}

	ngOnDestroy() {
		this.subscriptions.forEach((s) => s.unsubscribe());
		this.destroySubscriptions$.next(true);
		this.destroySubscriptions$.unsubscribe();
	}

	emisorToRequest(datos: DatosEmisorBhe) {
		this.newBhe.emisorId = datos.emisorId;
		this.newBhe.direccionCodigoEmisor = datos.codigoDireccionEmisor;
		this.newBhe.comunaEmisor = datos.comunaEmisor;
		this.newBhe.direccionEmisor = datos.direccionEmisor;
		this.newBhe.terceros = datos.terceros;
		if (datos.terceros) {
			this.newBhe.retencionPorEmisor = false;
			this.actualizarRetencion(this.newBhe.retencionPorEmisor);
			this.newBhe.personaAutorizada = datos.personaAutorizada.personaAutorizadaCesionId;
		} else {
			this.newBhe.personaAutorizada = undefined;
		}
	}

	datosCreacionToRequest(datos: DatosCreacionBhe) {
		if (datos.receptorBhe) {
			this.newBhe.rutReceptor = datos.receptorBhe.rutReceptor;
			this.newBhe.dvReceptor = datos.receptorBhe.dvReceptor;
			this.newBhe.nombreReceptor = datos.receptorBhe.nombreReceptor;
			this.newBhe.direccionReceptor = datos.receptorBhe.direccionReceptor;
			this.newBhe.regionCodigoReceptor = datos.receptorBhe.codigoRegionReceptor;
			this.newBhe.regionReceptor = datos.receptorBhe.regionReceptor;
			this.newBhe.comunaReceptor = datos.receptorBhe.comunaReceptor;
		}
		this.newBhe.fechaEmision = datos.fechaEmision;
		let retencionEmisor: boolean = datos.retencionEmisor;
		this.newBhe.retencionPorEmisor = retencionEmisor;
		if (this.newBhe.retenido != undefined && this.newBhe.retenido > 0) {
			this.actualizarRetencion(this.newBhe.retencionPorEmisor);
		}
	}

	resumenBheToRequest(resumen: ResumenDetallesBhe) {
		this.newBhe.detalles = resumen.detalles;
		this.newBhe.bruto = resumen.bruto;
		this.newBhe.total = resumen.total;
		this.newBhe.liquido = resumen.liquido;
		this.newBhe.retenido = resumen.montoRetencion;
		this.actualizarRetencion(this.newBhe.retencionPorEmisor);
	}

	actualizarRetencion(retencionDeEmisor: boolean) {
		if (retencionDeEmisor) {
			this.newBhe.retencionEmisor = this.newBhe.retenido;
			this.newBhe.retencionReceptor = 0;
		} else {
			this.newBhe.retencionEmisor = 0;
			this.newBhe.retencionReceptor = this.newBhe.retenido;
		}
	}

	//validations
	validEmisorDataInRequest(): boolean {
		return this.newBhe.emisorId != undefined && this.newBhe.emisorId != "" && ((this.newBhe.direccionCodigoEmisor != undefined && !this.newBhe.terceros) || (this.newBhe.terceros && this.newBhe.personaAutorizada != undefined));
	}

	validCreacionDataInRequest() {
		return this.newBhe.rutReceptor != undefined && this.newBhe.nombreReceptor != undefined && this.newBhe.nombreReceptor != "" && this.newBhe.regionCodigoReceptor != undefined && this.newBhe.comunaReceptor != undefined && this.newBhe.comunaReceptor != "";
	}

	validDetallesListInRequest() {
		return this.newBhe.detalles != undefined && this.newBhe.detalles.length > 0;
	}

	validResumenDataInRequest() {
		return this.newBhe.total > 0 && this.newBhe.bruto > 0 && ((this.newBhe.retencionPorEmisor && this.newBhe.retencionEmisor > 0) || (!this.newBhe.retencionPorEmisor && this.newBhe.retencionReceptor > 0));
	}

	validResumenDetallesDataInRequest() {
		return this.newBhe.detalles != undefined && this.newBhe.detalles.length > 0 && this.newBhe.liquido != undefined && this.newBhe.liquido > 5;
	}
}
